import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import SplitPaymentFields from '../common/SplitPaymentFields';
import CustomerDropdown from '../customers/CustomerDropdown';
import { fieldLabelSx } from '../common/formFieldStyles';
import { getLocalizedValue } from '../../constants/Languages';
import PendingConsentAlert from '../consent-forms/PendingConsentAlert';
import { DEFAULT_CURRENCY, formatCurrency } from '../../utils/currencyUtils';
import {
  createDefaultSplitPaymentLines,
  toCheckoutPayload,
  toSplitAmount,
  validateSplitPaymentLines,
} from '../../utils/splitPaymentUtils';
import { BRAND } from '../../theme/brandTokens';

const EnrollCustomerMembershipDialog = ({
  open,
  onClose,
  onEnroll,
  enrolling,
  storeId,
  activePlans,
  currencyCode = DEFAULT_CURRENCY,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [planId, setPlanId] = useState('');
  const [lines, setLines] = useState(() => createDefaultSplitPaymentLines(0));
  const [errors, setErrors] = useState({});

  const selectedPlan = useMemo(
    () => activePlans.find((plan) => plan.id === planId) || null,
    [activePlans, planId],
  );
  const amountDue = Math.max(0, toSplitAmount(selectedPlan?.price));

  useEffect(() => {
    if (!open) {
      setSelectedCustomer(null);
      setPlanId('');
      setLines(createDefaultSplitPaymentLines(0));
      setErrors({});
    }
  }, [open]);

  useEffect(() => {
    if (!open || !planId) {
      return;
    }

    setLines(createDefaultSplitPaymentLines(amountDue));
    setErrors((current) => {
      const next = { ...current };
      delete next.planId;
      delete next.total;
      return next;
    });
  }, [open, planId, amountDue]);

  const handleSubmit = () => {
    const nextErrors = {};

    if (!selectedCustomer) {
      nextErrors.customer = 'customerMembershipsManagement.enroll.errors.customerRequired';
    }

    if (!planId) {
      nextErrors.planId = 'customerMembershipsManagement.enroll.errors.planRequired';
    }

    const paymentResult = validateSplitPaymentLines({
      lines,
      amountDue,
      allowDiscount: false,
    });

    Object.assign(nextErrors, paymentResult.errors);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onEnroll({
      customerId: selectedCustomer.customerId,
      planId,
      ...toCheckoutPayload({
        payments: paymentResult.payments,
        collected: paymentResult.collected,
        amountDue,
      }),
    });
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={enrolling ? undefined : onClose}
      maxWidth="md"
      title={t('customerMembershipsManagement.enroll.title')}
      subtitle={t('customerMembershipsManagement.enroll.description')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{
        flexShrink: 0,
        justifyContent: 'space-between',
      }}
      actions={(
        <>
          <Button onClick={onClose} disabled={enrolling} color="inherit">
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton
            onClick={handleSubmit}
            disabled={enrolling}
            startIcon={enrolling ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {enrolling
              ? t('customerMembershipsManagement.enroll.enrolling')
              : t('customerMembershipsManagement.enroll.confirm')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <CustomerDropdown
          storeId={storeId}
          enabled={open}
          label={t('customerMembershipsManagement.enroll.customer')}
          placeholder={t('customerMembershipsManagement.enroll.customerPlaceholder')}
          value={selectedCustomer}
          onChange={setSelectedCustomer}
          error={Boolean(errors.customer)}
          helperText={errors.customer ? t(errors.customer) : ''}
          disabled={enrolling}
          required
        />

        <SalovareSelectField
          label={t('customerMembershipsManagement.enroll.plan')}
          value={planId}
          onChange={(event) => setPlanId(event.target.value)}
          displayEmpty
          error={Boolean(errors.planId)}
          helperText={errors.planId ? t(errors.planId) : ''}
          renderValue={(value) => {
            if (!value) {
              return (
                <Typography component="span" sx={{ color: BRAND.textSubtle }}>
                  {t('customerMembershipsManagement.enroll.planPlaceholder')}
                </Typography>
              );
            }

            const plan = activePlans.find((item) => item.id === value);
            return getLocalizedValue(
              plan?.name,
              i18n.language,
              undefined,
              t('customer.memberships.card.unnamed'),
            );
          }}
        >
          {activePlans.map((plan) => (
            <MenuItem key={plan.id} value={plan.id}>
              {getLocalizedValue(plan.name, i18n.language, undefined, t('customer.memberships.card.unnamed'))}
            </MenuItem>
          ))}
        </SalovareSelectField>

        {planId ? (
          <PendingConsentAlert
            id="enroll-membership-consent-alert"
            storeId={storeId}
            customerId={selectedCustomer?.customerId}
            membershipPlanId={planId}
            variant="purchase"
          />
        ) : null}

        {planId ? (
          <>
            <Box>
              <Typography variant="subtitle2" sx={fieldLabelSx}>
                {t('appointmentCheckout.amountDue')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.accentText }}>
                {formatCurrency(amountDue, selectedPlan?.currency || currencyCode)}
              </Typography>
            </Box>
            <SplitPaymentFields
              lines={lines}
              onChange={setLines}
              amountDue={amountDue}
              currencyCode={selectedPlan?.currency || currencyCode}
              allowDiscount={false}
              disabled={enrolling}
              errors={errors}
            />
          </>
        ) : null}
      </Box>
    </ScrollableDialog>
  );
};

export default EnrollCustomerMembershipDialog;
